import { differenceInYears, format } from 'date-fns';
import * as Yup from 'yup';
import {
  optionalEmailValidation,
  formatPhoneForInput,
  phoneValidation,
} from '@/shared/utils/validation';
import { vnAddressValidation, DEFAULT_VN_COUNTRY, isVietnamCountry } from '@/shared/utils/vnAddress';
import { presentPatientPlace } from './patientPlace';
import { formatPersonName, type PersonNameParts } from '@/shared/utils/personName';
import type { Patient } from '@/shared/types/patient';
import { PATIENT_NOTES_MAX_LENGTH } from '@/shared/constants/personFields';
import type { ReferredByPickerValue } from '@/modules/referrer/types';
import {
  buildReferredByWritePayload,
  summaryToPickerValue,
} from '@/modules/referrer/utils/referredByPicker';

/** Matches backend `PATIENT_ID_MAX_LEN` (create route + CSV import). */
export const PATIENT_ID_MAX_LENGTH = 20;

export type PatientFormMode = 'create' | 'edit';

const foldCustomerSourceKey = (raw: string): string =>
  raw
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

/**
 * Catalog source is a referral channel ("Giới thiệu").
 * Báo cáo người giới thiệu dùng cùng quy tắc trong `referrerReport.ts`.
 * Form bệnh nhân vẫn luôn cho nhập người giới thiệu, không phụ thuộc nguồn.
 */
export const isReferralCustomerSource = (
  source?: { code?: string; name?: string } | null
): boolean => {
  if (!source) return false;
  const code = foldCustomerSourceKey(source.code ?? '').replace(/ /g, '-');
  if (code === 'friend' || code === 'referral' || code === 'gioi-thieu') return true;
  return foldCustomerSourceKey(source.name ?? '').includes('gioi thieu');
};

// Medical history preset keys (translated at render time via t('medicalHistoryPresets.<key>')).
// Shown as checkboxes on the patient form.
export const MEDICAL_HISTORY_HIGH_RISK_KEYS = [
  'pregnant',
  'diabetes',
  'heart',
  'drugAllergy',
  'hypertension',
  'bleeding',
] as const;

export const MEDICAL_HISTORY_OTHER_KEYS = [
  'antibioticAllergy',
  'stomach',
  'asthma',
] as const;

export const MEDICAL_HISTORY_PRESET_KEYS = [
  ...MEDICAL_HISTORY_HIGH_RISK_KEYS,
  ...MEDICAL_HISTORY_OTHER_KEYS,
] as const;

/** Older charts stored the previous Vietnamese label. */
const MEDICAL_HISTORY_STORED_ALIASES: Record<string, string> = {
  'Dị ứng thuốc': 'Dị ứng thuốc tê',
  'Chảy máu kéo dài': 'Chảy máu lâu đông',
};

export const RELATIONSHIP_TYPE_VALUES = [
  'parent',
  'spouse',
  'child',
  'sibling',
  'relative',
  'nephew_niece',
  'friend',
  'colleague',
  'other',
] as const;

/** Matches backend `SOCIAL_PLATFORMS`. One account per platform. */
export const SOCIAL_PLATFORM_VALUES = [
  'zalo',
  'facebook',
  'instagram',
  'threads',
  'tiktok',
  'whatsapp',
  'telegram',
  'x',
] as const;

export type SocialPlatform = (typeof SOCIAL_PLATFORM_VALUES)[number];

/** Matches backend `HEALTH_INSURANCE_NUMBER_MAX_LENGTH`. */
export const HEALTH_INSURANCE_NUMBER_MAX_LENGTH = 20;

/** Matches backend `PATIENT_BLOOD_TYPES`. */
export const PATIENT_BLOOD_TYPES = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'] as const;
export type PatientBloodType = (typeof PATIENT_BLOOD_TYPES)[number];

/** Matches backend `PATIENT_WEIGHT_KG_MIN` / `PATIENT_WEIGHT_KG_MAX`. One decimal place, kilograms. */
export const PATIENT_WEIGHT_KG_MIN = 3;
export const PATIENT_WEIGHT_KG_MAX = 150;

const WEIGHT_KG_TEXT = /^\d{1,3}(\.\d)?$/;

/** Empty → null. A weight inside the allowed range → number. Anything else → null. */
export const parseWeightKgInput = (raw: string): number | null => {
  const text = raw.trim().replace(',', '.').replace(/kg$/i, '').trim();
  if (!text || !WEIGHT_KG_TEXT.test(text)) return null;
  const value = Number(text);
  if (value < PATIENT_WEIGHT_KG_MIN || value > PATIENT_WEIGHT_KG_MAX) return null;
  return value;
};

/** Matches backend `SOCIAL_ACCOUNT_VALUE_MAX_LENGTH`. */
export const SOCIAL_ACCOUNT_VALUE_MAX_LENGTH = 200;

export interface PatientSocialAccountFormValue {
  platform: SocialPlatform;
  value: string;
}

/** API value → i18n key under `relationshipTypes.*` (nephew_niece → nephewNiece). */
export const RELATIONSHIP_TYPE_I18N_KEY: Record<string, string> = {
  nephew_niece: 'nephewNiece',
};

export interface PatientRelationshipFormValue {
  patient: any | null;
  relationshipType: string;
}

export interface PatientFormValues {
  firstName: string;
  lastName: string;
  /** Custom patient code (PA prefix). Empty → backend auto-generates. Immutable in edit mode. */
  patientId: string;
  email: string;
  phone: string;
  alternatePhone: string;
  citizenId: string;
  /** Số thẻ bảo hiểm y tế. */
  healthInsuranceNumber: string;
  /** Kilograms, typed as text so an empty chart stays blank. */
  weightKg: string;
  bloodType: '' | PatientBloodType;
  /** Ngoại kiều. Mutually exclusive with `isVietKieu`. */
  isForeigner: boolean;
  /** Việt kiều. Same residence fields as Ngoại kiều. */
  isVietKieu: boolean;
  gender: string;
  dateOfBirth: Date | null;
  street: string;
  province: string;
  ward: string;
  provinceCode: string;
  wardCode: string;
  /** Home country. Used when `isForeigner` or `isVietKieu`. */
  residenceCountry: string;
  /** Home state/province. Used when they live abroad and the home country is not Vietnam. */
  residenceRegion: string;
  medicalHistory: string[];
  guardianName: string;
  guardianPhone: string;
  /** Registered clinic id (maps to `registeredClinic` in the API payload). */
  clinicId: string;
  occupation: string;
  notes: string;
  referredBy: ReferredByPickerValue | null;
  customerSource: string;
  customerGroup: '' | 'implant' | 'ceramic' | 'ortho' | 'pediatric' | 'general';
  relationships: PatientRelationshipFormValue[];
  socialAccounts: PatientSocialAccountFormValue[];
}

export const EMPTY_PATIENT_FORM_VALUES: PatientFormValues = {
  firstName: '',
  lastName: '',
  patientId: '',
  email: '',
  phone: '',
  alternatePhone: '',
  citizenId: '',
  healthInsuranceNumber: '',
  weightKg: '',
  bloodType: '',
  isForeigner: false,
  isVietKieu: false,
  gender: '',
  dateOfBirth: null,
  street: '',
  province: '',
  ward: '',
  provinceCode: '',
  wardCode: '',
  residenceCountry: '',
  residenceRegion: '',
  medicalHistory: [],
  guardianName: '',
  guardianPhone: '',
  clinicId: '',
  occupation: '',
  notes: '',
  referredBy: null,
  customerSource: '',
  customerGroup: '',
  relationships: [],
  socialAccounts: [],
};

/** Draft values carried onto the list so the create dialog can open already filled. */
export type AddPatientPrefill = {
  firstName?: string;
  phone?: string;
  gender?: string;
  dateOfBirth?: string;
  clinicId?: string;
};

/** History state on `/patients`. Consumed once, then cleared. */
export type AddPatientLocationState = {
  openAddPatient?: boolean;
  prefill?: AddPatientPrefill;
};

type TranslateFn = (...args: any[]) => string;

const refId = (value: unknown): string => {
  if (value == null) return '';
  if (typeof value === 'string') return value;
  const record = value as { _id?: string; id?: string };
  return String(record._id || record.id || '');
};

/**
 * Legacy fallback for patients that only have the combined `name`.
 * Vietnamese order is "Họ (lastName) Tên (firstName)", so the last word is the given name.
 */
export const splitLegacyName = (
  name: string | undefined | null
): { firstName: string; lastName: string } => {
  const parts = (name ?? '').trim().split(/\s+/).filter(Boolean);
  if (parts.length <= 1) {
    return { firstName: parts[0] ?? '', lastName: '' };
  }
  return {
    firstName: parts[parts.length - 1],
    lastName: parts.slice(0, -1).join(' '),
  };
};

/** Display name preferring the backend-computed `name`, falling back to first/last parts. */
export const displayPatientName = (
  patient:
    | Pick<Patient, 'name' | 'firstName' | 'lastName'>
    | PersonNameParts
    | null
    | undefined
): string => {
  if (!patient) return '';
  if ('name' in patient && patient.name) return patient.name;
  return formatPersonName(patient);
};

export const mapPatientToFormValues = (patient: Patient | null): PatientFormValues => {
  if (!patient) return EMPTY_PATIENT_FORM_VALUES;

  const hasNameParts = Boolean(patient.firstName || patient.lastName);
  const legacyParts = hasNameParts ? null : splitLegacyName(patient.name);
  const place = presentPatientPlace({
    isForeigner: patient.isForeigner,
    isVietKieu: patient.isVietKieu,
    address: patient.address,
    residenceCountry: patient.residenceCountry,
    residenceRegion: patient.residenceRegion,
  });

  return {
    firstName: patient.firstName || legacyParts?.firstName || '',
    lastName: patient.lastName || legacyParts?.lastName || '',
    patientId: patient.patientId || '',
    email: patient.email || '',
    phone: formatPhoneForInput(patient.phone),
    alternatePhone: formatPhoneForInput(patient.alternatePhone),
    citizenId: patient.citizenId || '',
    healthInsuranceNumber: patient.healthInsuranceNumber || '',
    weightKg: patient.weightKg != null ? String(patient.weightKg) : '',
    bloodType: patient.bloodType ?? '',
    isForeigner: place.isForeigner,
    isVietKieu: place.isVietKieu,
    gender: patient.gender || '',
    dateOfBirth: patient.dateOfBirth ? new Date(patient.dateOfBirth) : null,
    street: place.address.street,
    province: place.address.province,
    ward: place.address.ward,
    provinceCode: place.address.provinceCode,
    wardCode: place.address.wardCode,
    residenceCountry: place.residenceCountry,
    residenceRegion: place.residenceRegion,
    medicalHistory: Array.isArray(patient.medicalHistory)
      ? patient.medicalHistory.map((item) => MEDICAL_HISTORY_STORED_ALIASES[item] ?? item)
      : [],
    guardianName: patient.guardian?.name || '',
    guardianPhone: formatPhoneForInput(patient.guardian?.phone),
    clinicId: refId(patient.registeredClinic),
    occupation: patient.occupation || '',
    notes: patient.notes || '',
    referredBy: patient.referredBy?.id ? summaryToPickerValue(patient.referredBy) : null,
    customerSource: (() => {
      const source = patient.customerSource as
        | string
        | { _id?: string; id?: string }
        | null
        | undefined;
      if (!source) return '';
      if (typeof source === 'object') {
        return String(source._id || source.id || '');
      }
      return String(source);
    })(),
    customerGroup: patient.customerGroup ?? '',
    relationships: patient.relationships || [],
    socialAccounts: (patient.socialAccounts || [])
      .filter(
        (account): account is { platform: SocialPlatform; value: string } =>
          SOCIAL_PLATFORM_VALUES.includes(account.platform as SocialPlatform) &&
          Boolean(account.value?.trim())
      )
      .map((account) => ({
        platform: account.platform,
        value: account.value.trim(),
      })),
  };
};

/**
 * Shared Add/Edit schema. firstName, lastName và clinic là bắt buộc.
 * gender, dateOfBirth, phone và địa chỉ (street/ward/province) đều tùy chọn — phone/email
 * vẫn được kiểm tra định dạng khi có nhập.
 */
export const buildPatientValidationSchema = (t: TranslateFn) =>
  Yup.object({
    firstName: Yup.string().trim().required(t('validation.firstNameRequired')),
    lastName: Yup.string().trim().required(t('validation.lastNameRequired')),
    patientId: Yup.string()
      .trim()
      .max(
        PATIENT_ID_MAX_LENGTH,
        t('validation.patientIdMax', { max: PATIENT_ID_MAX_LENGTH })
      ),
    email: optionalEmailValidation,
    phone: phoneValidation,
    alternatePhone: phoneValidation,
    citizenId: Yup.string(),
    healthInsuranceNumber: Yup.string().max(
      HEALTH_INSURANCE_NUMBER_MAX_LENGTH,
      t('validation.healthInsuranceNumberMax', { max: HEALTH_INSURANCE_NUMBER_MAX_LENGTH })
    ),
    weightKg: Yup.string().test(
      'weight-kg',
      t('validation.weightKgMax', { min: PATIENT_WEIGHT_KG_MIN, max: PATIENT_WEIGHT_KG_MAX }),
      (value) => !value?.trim() || parseWeightKgInput(value) != null
    ),
    bloodType: Yup.string().oneOf(['', ...PATIENT_BLOOD_TYPES]),
    isForeigner: Yup.boolean().test(
      'origin-exclusive',
      t('validation.originExclusive'),
      function (value) {
        return !(value && this.parent.isVietKieu);
      }
    ),
    isVietKieu: Yup.boolean().test(
      'origin-exclusive',
      t('validation.originExclusive'),
      function (value) {
        return !(value && this.parent.isForeigner);
      }
    ),
    gender: Yup.string(),
    dateOfBirth: Yup.date().nullable(),
    ...vnAddressValidation,
    street: Yup.string(),
    ward: Yup.string(),
    province: Yup.string(),
    residenceCountry: Yup.string().test(
      'residence-required',
      t('validation.residenceCountryRequired'),
      function (value) {
        const { isForeigner, isVietKieu } = this.parent as {
          isForeigner?: boolean;
          isVietKieu?: boolean;
        };
        if (!isForeigner && !isVietKieu) return true;
        return Boolean(value?.trim());
      }
    ),
    residenceRegion: Yup.string(),
    medicalHistory: Yup.array().of(Yup.string()),
    notes: Yup.string().max(
      PATIENT_NOTES_MAX_LENGTH,
      t('validation.notesMax', { max: PATIENT_NOTES_MAX_LENGTH })
    ),
    guardianName: Yup.string(),
    guardianPhone: phoneValidation,
    clinicId: Yup.string().required(t('validation.clinicRequired')),
    relationships: Yup.array().of(
      Yup.object({
        patient: Yup.object().nullable(),
        relationshipType: Yup.string(),
      })
    ),
    socialAccounts: Yup.array()
      .of(
        Yup.object({
          platform: Yup.string().oneOf([...SOCIAL_PLATFORM_VALUES]),
          value: Yup.string().max(
            SOCIAL_ACCOUNT_VALUE_MAX_LENGTH,
            t('validation.socialAccountMax', { max: SOCIAL_ACCOUNT_VALUE_MAX_LENGTH })
          ),
        })
      )
      .test('unique-platform', t('validation.socialPlatformDuplicate'), (accounts) => {
        const platforms = (accounts ?? [])
          .map((account) => account?.platform)
          .filter((platform): platform is NonNullable<typeof platform> => Boolean(platform));
        return new Set(platforms).size === platforms.length;
      }),
  });

export const calculatePatientAge = (dateOfBirth: Date | null): number | null =>
  dateOfBirth ? differenceInYears(new Date(), new Date(dateOfBirth)) : null;

const formatDateOfBirth = (dateOfBirth: Date | null): string | null =>
  dateOfBirth ? format(new Date(dateOfBirth), 'yyyy-MM-dd') : null;

const buildAddress = (values: PatientFormValues) => ({
  street: values.street,
  province: values.province,
  ward: values.ward,
  provinceCode: values.provinceCode || undefined,
  wardCode: values.wardCode || undefined,
  country: DEFAULT_VN_COUNTRY,
});

const livesAbroad = (values: PatientFormValues) => values.isForeigner || values.isVietKieu;

const buildResidence = (values: PatientFormValues): Record<string, string> => {
  if (!livesAbroad(values)) {
    return { residenceCountry: '', residenceRegion: '' };
  }
  return {
    residenceCountry: values.residenceCountry.trim(),
    residenceRegion: isVietnamCountry(values.residenceCountry)
      ? ''
      : values.residenceRegion.trim(),
  };
};

const buildReferredBy = (values: PatientFormValues) =>
  buildReferredByWritePayload(values.referredBy);

const buildRelationships = (values: PatientFormValues) =>
  (values.relationships || [])
    .filter((rel) => rel.patient !== null)
    .map((rel) => ({
      patient: refId(rel.patient),
      relationshipType: rel.relationshipType,
    }));

const buildSocialAccounts = (values: PatientFormValues) => {
  const seen = new Set<string>();
  const accounts: PatientSocialAccountFormValue[] = [];
  for (const account of values.socialAccounts || []) {
    const value = account.value.trim();
    if (!value || seen.has(account.platform)) continue;
    if (!SOCIAL_PLATFORM_VALUES.includes(account.platform)) continue;
    seen.add(account.platform);
    accounts.push({ platform: account.platform, value });
  }
  return accounts;
};

/** Create payload: sends firstName/lastName (no `name`) and omits empty optional fields. */
export const buildCreatePatientPayload = (
  values: PatientFormValues,
  now: Date = new Date()
): Record<string, unknown> => {
  const payload: Record<string, unknown> = {
    firstName: values.firstName.trim(),
    lastName: values.lastName.trim(),
    registrationDate: format(now, 'yyyy-MM-dd'),
    registeredClinic: values.clinicId,
    address: buildAddress(values),
    isForeigner: values.isForeigner,
    isVietKieu: values.isVietKieu,
    ...buildResidence(values),
  };
  const dateOfBirth = formatDateOfBirth(values.dateOfBirth);
  if (dateOfBirth) {
    payload.dateOfBirth = dateOfBirth;
    payload.age = calculatePatientAge(values.dateOfBirth);
  }

  if (values.gender) payload.gender = values.gender;
  if (values.patientId.trim()) payload.patientId = values.patientId.trim().toUpperCase();
  if (values.phone) payload.phone = values.phone;
  if (values.email) payload.email = values.email;
  if (values.alternatePhone) payload.alternatePhone = values.alternatePhone;
  if (values.citizenId) payload.citizenId = values.citizenId;
  if (values.healthInsuranceNumber.trim()) {
    payload.healthInsuranceNumber = values.healthInsuranceNumber.trim();
  }
  const weightKg = parseWeightKgInput(values.weightKg);
  if (weightKg != null) payload.weightKg = weightKg;
  if (values.bloodType) payload.bloodType = values.bloodType;
  if (values.occupation) payload.occupation = values.occupation;
  if (values.notes.trim()) payload.notes = values.notes.trim();
  if (values.customerSource) payload.customerSource = values.customerSource;
  if (values.customerGroup) payload.customerGroup = values.customerGroup;
  if (values.medicalHistory.length > 0) payload.medicalHistory = values.medicalHistory;
  if (values.guardianName || values.guardianPhone) {
    payload.guardian = { name: values.guardianName, phone: values.guardianPhone };
  }

  const relationships = buildRelationships(values);
  if (relationships.length > 0) payload.relationships = relationships;

  const socialAccounts = buildSocialAccounts(values);
  if (socialAccounts.length > 0) payload.socialAccounts = socialAccounts;

  const referredBy = buildReferredBy(values);
  if (referredBy) payload.referredBy = referredBy;

  return payload;
};

/** Update payload: sends firstName/lastName and explicit null for cleared referredBy/customerSource. */
export const buildUpdatePatientPayload = (
  values: PatientFormValues
): Record<string, unknown> => ({
  firstName: values.firstName.trim(),
  lastName: values.lastName.trim(),
  email: values.email,
  phone: values.phone,
  alternatePhone: values.alternatePhone,
  citizenId: values.citizenId,
  healthInsuranceNumber: values.healthInsuranceNumber.trim(),
  weightKg: parseWeightKgInput(values.weightKg),
  bloodType: values.bloodType || null,
  isForeigner: values.isForeigner,
  isVietKieu: values.isVietKieu,
  ...buildResidence(values),
  gender: values.gender || null,
  dateOfBirth: formatDateOfBirth(values.dateOfBirth),
  age: calculatePatientAge(values.dateOfBirth),
  address: buildAddress(values),
  occupation: values.occupation,
  notes: values.notes.trim(),
  referredBy: buildReferredBy(values),
  customerSource: values.customerSource || null,
  customerGroup: values.customerGroup || null,
  registeredClinic: values.clinicId,
  guardian: { name: values.guardianName, phone: values.guardianPhone },
  medicalHistory: values.medicalHistory,
  relationships: buildRelationships(values),
  socialAccounts: buildSocialAccounts(values),
});
